"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isIntersecting = exports.uniqueObjectsList = exports.uniqueItems = exports.uniqueIds = exports.recursiveFlattenObject = exports.recursiveFlattenArray = exports.isArrayEmpty = exports.insertToMapArray = exports.insertIf = exports.areArraysEqual = exports.arraysEquals = exports.arrayToMap = void 0;
const lodash_1 = require("lodash");
/**
 * Recursively flattens an array of objects based on the specified properties.
 *
 * @template T - The type of the elements in the array.
 * @param {T[]} array - The array to flatten.
 * @param {string[]} properties - The properties to flatten the array based on.
 * @returns {T[]} - The flattened array.
 */
function recursiveFlattenArray(array, properties) {
    const results = [];
    for (const item of array ?? []) {
        if (item && typeof item === 'object') {
            results.push(...recursiveFlattenObject(item, properties));
        }
    }
    return results;
}
exports.recursiveFlattenArray = recursiveFlattenArray;
/**
 * Recursively flattens an object by extracting specified properties.
 *
 * @template T - The type of the object being flattened.
 * @param entity - The object to be flattened.
 * @param properties - The properties to be extracted from the object.
 * @returns An array of flattened objects.
 */
function recursiveFlattenObject(entity, properties) {
    if (!entity || typeof entity !== 'object') {
        return [];
    }
    const results = [entity];
    for (const property of properties ?? []) {
        const value = entity[property];
        if (Array.isArray(value)) {
            results.push(...recursiveFlattenArray(value, properties));
        }
        else if (value && typeof value === 'object') {
            results.push(...recursiveFlattenObject(value, properties));
        }
    }
    return results;
}
exports.recursiveFlattenObject = recursiveFlattenObject;
/**
 * Inserts elements into an array if a condition is true.
 *
 * @template T - The type of elements in the array.
 * @param {boolean} condition - The condition to check.
 * @param {...T[]} elements - The elements to insert into the array.
 * @returns {T[]} - The resulting array.
 */
function insertIf(condition, ...elements) {
    return condition ? elements : [];
}
exports.insertIf = insertIf;
/**
 * Returns an array of unique IDs from the provided arrays of IDs.
 *
 * @param idsArrays - Arrays of IDs to be merged and filtered.
 * @returns An array of unique IDs.
 */
function uniqueIds(...idsArrays) {
    return uniqueItems(...idsArrays);
}
exports.uniqueIds = uniqueIds;
/**
 * Returns an array of unique items across all provided arrays, removing only `null` and `undefined` values.
 *
 * @template T - The type of elements in the arrays.
 * @param {...T[][]} arrays - Arrays of items to be merged and deduplicated.
 * @returns {T[]} - A deduplicated array of unique items, excluding only `null` and `undefined`.
 *
 * @example
 * // returns [0, 1, 2, false, NaN, ""]
 * uniqueItemsFilterNullish([0, 1, 2], [null, undefined, false, NaN, ""]);
 */
function uniqueItems(...arrays) {
    return [
        ...new Set((arrays ?? [])
            .reduce((result, items) => {
            return [...result, ...(items ?? [])];
        }, [])
            .filter(item => item !== null && item !== undefined)),
    ];
}
exports.uniqueItems = uniqueItems;
/**
 * Creates a unique list of objects from multiple arrays based on specified keys.
 *
 * @param {Array<keyof T>} groupKeys - The keys to group by for uniqueness.
 * @param {...T[]} arrays - The arrays of objects to process.
 * @returns {T[]} A unique array of objects based on the provided group keys.
 *
 * @template T - The type of objects in the input arrays.
 *
 * @throws {TypeError} Throws an error if any of the arrays contain items without the specified keys.
 */
function uniqueObjectsList(groupKeys, ...arrays) {
    if (!groupKeys || groupKeys.length === 0) {
        throw new Error('groupKeys must contain at least one key.');
    }
    const result = new Map();
    (arrays ?? []).forEach((array) => {
        (array ?? []).forEach((item) => {
            const key = groupKeys.map((k) => item[k]).join('-');
            if (key && !result.has(key)) {
                result.set(key, item);
            }
        });
    });
    return Array.from(result.values());
}
exports.uniqueObjectsList = uniqueObjectsList;
/**
 * Compares the first array with multiple other arrays to determine if they are all equal.
 * Uses a deep comparison method (isEqual) to check the contents of the arrays.
 *
 * @deprecated Use `areArraysEqual` instead.
 * @param {any[]} firstArray - The first array to compare against the others.
 * @param {...any[][]} arrays - The other arrays to compare to the first array.
 * @returns {boolean} - Returns true if all arrays are equal; otherwise, false.
 *
 * @example
 * arraysEquals([1, 2, 3], [1, 2, 3], [1, 2, 3]); // returns true
 * arraysEquals([1, 2], [1, 2, 3]); // returns false
 * arraysEquals([], [], []); // returns true
 */
function arraysEquals(firstArray, ...arrays) {
    return arrays.every((array) => (0, lodash_1.isEqual)(firstArray, array));
}
exports.arraysEquals = arraysEquals;
/**
 * Compares an array with one or more arrays to determine if they are all equal.
 *
 * @param {T[]} firstArray - The first array to compare against.
 * @param {...T[][]} arrays - One or more additional arrays to compare.
 * @returns {boolean} True if all arrays are equal, false otherwise.
 *
 * @throws {TypeError} Throws an error if any of the inputs are not arrays.
 *
 * @template T - The type of elements in the arrays.
 */
function areArraysEqual(firstArray, ...arrays) {
    if (!Array.isArray(firstArray)) {
        throw new TypeError('The first argument must be an array.');
    }
    arrays.forEach((array) => {
        if (!Array.isArray(array)) {
            throw new TypeError('All additional arguments must be arrays.');
        }
    });
    return arrays.every((array) => (0, lodash_1.isEqual)(firstArray, array));
}
exports.areArraysEqual = areArraysEqual;
/**
 * Checks if the provided input is an empty array.
 *
 * @param {any[]} [array] - The input to check, which can be an array or undefined.
 * @returns {boolean} Returns true if the input is an empty array, false otherwise.
 *
 * @example
 * isArrayEmpty([]); // returns true
 * isArrayEmpty([1, 2, 3]); // returns false
 * isArrayEmpty(null); // returns false
 */
function isArrayEmpty(array) {
    return Array.isArray(array) && !array.length;
}
exports.isArrayEmpty = isArrayEmpty;
/**
 * Inserts a value into an array associated with a key in a Map.
 * If the key does not exist, it initializes it with an empty array.
 *
 * @param {Map<K, V[]>} map - The Map to modify, where K is the key type and V is the value type.
 * @param {K} key - The key under which to store the value.
 * @param {V} value - The value to insert into the array associated with the key.
 * @throws {TypeError} Throws an error if the provided map is not a Map instance.
 *
 * @template K - The type of the keys in the Map (defaults to string).
 * @template V - The type of the values in the Map (defaults to any).
 */
function insertToMapArray(map, key, value) {
    if (!map.has(key)) {
        map.set(key, []);
    }
    map.get(key).push(value);
}
exports.insertToMapArray = insertToMapArray;
/**
 * Converts an array of objects into a Map, where the keys are the `id` properties of the objects.
 *
 * @template T - The type of the objects in the array, which must extend an object containing a numeric `id` property.
 * @param {T[] | null | undefined} array - The array of objects to convert. Can be null or undefined, in which case an empty Map is returned.
 * @returns {Map<number, T>} A Map where the keys are the `id` values from the objects and the values are the objects themselves.
 *
 * @example
 * const input = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }];
 * const result = arrayToMap(input);
 * // result is a Map with:
 * //   1 => { id: 1, name: 'Alice' }
 * //   2 => { id: 2, name: 'Bob' }
 *
 * @example
 * const emptyResult = arrayToMap([]);
 * // emptyResult is an empty Map
 *
 * @example
 * const nullResult = arrayToMap(null);
 * // nullResult is an empty Map
 */
function arrayToMap(array) {
    if (!array) {
        return new Map();
    }
    return new Map(array.map(entity => [entity.id, entity]));
}
exports.arrayToMap = arrayToMap;
/**
 * Checks if two arrays have any common elements.
 *
 * This function verifies whether the provided arrays intersect.
 * If either array is null or undefined, it returns false.
 * If both arrays are empty, it returns true, indicating they are considered to intersect.
 * If only one of the arrays is empty, it returns false.
 *
 * @template T - The type of elements in the arrays.
 * @param {T[] | undefined | null} array - The first array to compare, which can be an array of type T, undefined, or null.
 * @param {T[] | undefined | null} other - The second array to compare, which can be an array of type T, undefined, or null.
 * @returns {boolean} - Returns true if the arrays have common elements,
 *                      false if they do not or if either input is null/undefined.
 *
 * @example
 * returns true
 * isIntersecting([1, 2, 3], [3, 4, 5]);
 *
 * @example
 * returns false
 * isIntersecting([1, 2, 3], [4, 5, 6]);
 *
 * @example
 * returns true
 * isIntersecting([], []);
 *
 * @example
 * returns false
 * isIntersecting(null, [1, 2, 3]);
 *
 * @example
 * returns false
 * isIntersecting([1, 2, 3], undefined);
 */
function isIntersecting(array, other) {
    if (!array || !other) {
        return false;
    }
    if (!array.length && !other.length) {
        return true;
    }
    if (!array.length || !other.length) {
        return false;
    }
    const set = new Set(array.length < other.length ? array : other);
    const comparisonArray = array.length < other.length ? other : array;
    return comparisonArray.some((value) => set.has(value));
}
exports.isIntersecting = isIntersecting;
//# sourceMappingURL=array.util.js.map